import type { FinalisedVersion } from './finalised-version.js';
import { trialLifecycleMachine, type TrialLifecycleContext } from './machine.js';
import type { TrialStatus } from './stage.js';

export type TrialLifecycleAction =
  | 'modifyPack'
  | 'startGeneration'
  | 'triage'
  | 'finalise'
  | 'export'
  // F-149: a NON-mutating interim (draft) export available during review. It is a
  // derived availability over the in_review state (like `export`), NOT a machine
  // event — interim export changes no lifecycle state (§XI, FR-004).
  | 'interimExport';

export type ExportAvailability =
  | { readonly kind: 'absent' }
  | {
      readonly kind: 'unavailable';
      readonly reason: 'outstanding';
      readonly outstanding: number;
    }
  | {
      readonly kind: 'unavailable';
      readonly reason: 'changesSinceVersion';
      readonly version: FinalisedVersion;
    }
  | { readonly kind: 'unavailable'; readonly reason: 'readyToFinalise' }
  | { readonly kind: 'active'; readonly version: FinalisedVersion };

const restore = (status: TrialStatus, context: TrialLifecycleContext) =>
  trialLifecycleMachine.resolveState({ value: status, context });

export const canStartGeneration = (status: TrialStatus, context: TrialLifecycleContext): boolean =>
  restore(status, context).can({ type: 'START_GENERATION' });

export const canFinalise = (
  status: TrialStatus,
  context: TrialLifecycleContext,
  version: FinalisedVersion,
): boolean => restore(status, context).can({ type: 'FINALISE', version });

export const canModifyPack = (status: TrialStatus): boolean =>
  status === 'setting_up' || status === 'failed';

export const canTriage = (status: TrialStatus): boolean =>
  status === 'in_review' || status === 'finalised';

export const exportAvailability = (
  status: TrialStatus,
  context: TrialLifecycleContext,
): ExportAvailability => {
  if (status === 'finalised') {
    if (context.latestFinalisedVersion === null) {
      throw new Error('Finalised trials require a latest finalised version');
    }

    return { kind: 'active', version: context.latestFinalisedVersion };
  }

  if (status !== 'in_review') return { kind: 'absent' };

  if (context.ready < context.total) {
    return {
      kind: 'unavailable',
      reason: 'outstanding',
      outstanding: context.total - context.ready,
    };
  }

  if (context.latestFinalisedVersion !== null) {
    return {
      kind: 'unavailable',
      reason: 'changesSinceVersion',
      version: context.latestFinalisedVersion,
    };
  }

  return { kind: 'unavailable', reason: 'readyToFinalise' };
};

export const availableActions = (
  status: TrialStatus,
  context: TrialLifecycleContext,
): readonly TrialLifecycleAction[] => {
  const actions: TrialLifecycleAction[] = [];

  if (canModifyPack(status)) actions.push('modifyPack');
  if (canStartGeneration(status, context)) actions.push('startGeneration');
  if (canTriage(status)) actions.push('triage');
  if (status === 'in_review' && context.ready === context.total) actions.push('finalise');
  if (exportAvailability(status, context).kind === 'active') actions.push('export');
  // F-149: interim (draft) export is offered for every in_review trial (incl. one
  // reverted from finalised by a post-finalise edit). The "≥1 completed+included"
  // guard (FR-011) is a use-case/FE concern, not part of this projection (R4).
  if (status === 'in_review') actions.push('interimExport');

  return actions;
};
